// © 2025 Camp Promethean, LLC. All Rights Reserved.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import type Lenis from "lenis";

interface MarketingMotionContextValue {
  reducedMotion: boolean;
  lenis: Lenis | null;
}

const MarketingMotionContext = createContext<MarketingMotionContextValue>({
  reducedMotion: false,
  lenis: null,
});

export function useMarketingMotion() {
  return useContext(MarketingMotionContext);
}

interface MarketingMotionProviderProps {
  children: React.ReactNode;
}

export function MarketingMotionProvider({ children }: MarketingMotionProviderProps) {
  const location = useLocation();
  const lenisRef = useRef<Lenis | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [lenisReady, setLenisReady] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (isMobile) return;

    let cancelled = false;
    let frame = 0;
    let lenis: Lenis | null = null;
    let onLenisScroll: (() => void) | null = null;

    void (async () => {
      // Let the CSS hero entrance finish before starting smooth-scroll RAF.
      await new Promise<void>((resolve) => window.setTimeout(resolve, 900));

      const [{ default: LenisCtor }, { ScrollTrigger }] = await Promise.all([
        import("lenis"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return;

      lenis = new LenisCtor({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - 2 ** (-10 * t)),
        smoothWheel: true,
      });
      lenisRef.current = lenis;

      onLenisScroll = () => ScrollTrigger.update();
      lenis.on("scroll", onLenisScroll);

      const raf = (time: number) => {
        lenis?.raf(time);
        frame = requestAnimationFrame(raf);
      };
      frame = requestAnimationFrame(raf);
      setLenisReady(true);
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (lenis) {
        if (onLenisScroll) lenis.off("scroll", onLenisScroll);
        lenis.destroy();
      }
      lenisRef.current = null;
      setLenisReady(false);
    };
  }, [reducedMotion]);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
    import("gsap/ScrollTrigger").then(({ ScrollTrigger }) => {
      ScrollTrigger.refresh();
    });
  }, [location.pathname]);

  const value = useMemo(
    () => ({ reducedMotion, lenis: lenisReady ? lenisRef.current : null }),
    [reducedMotion, lenisReady],
  );

  return (
    <MarketingMotionContext.Provider value={value}>
      {children}
    </MarketingMotionContext.Provider>
  );
}

export default MarketingMotionProvider;
